import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Paper,
  Chip,
  Divider,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack, AddPhotoAlternate, Close } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useInquiryDetail, useReopenInquiry } from '../../hooks/useInquiry';
import type { InquiryComment, InquiryStatus } from '../../types/report';
import { uploadAttachments } from '../../utils/attachmentUpload';
import { formatDateTimeKST } from '../../utils/dateUtils';

const STATUS_CONFIG: Record<InquiryStatus, { label: string; color: 'default' | 'primary' | 'success' | 'warning' | 'error' }> = {
  SUBMITTED: { label: '접수됨', color: 'default' },
  IN_PROGRESS: { label: '처리 중', color: 'primary' },
  REPLIED: { label: '답변 완료', color: 'success' },
  CLOSED: { label: '종결', color: 'default' },
  REOPENED: { label: '추가 문의', color: 'warning' },
};

function CommentBubble({ comment }: { comment: InquiryComment }) {
  const isAdmin = comment.authorType === 'ADMIN';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isAdmin ? 'flex-start' : 'flex-end',
        mb: 2,
      }}
    >
      <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 0.5, px: 0.5 }}>
        {isAdmin ? 'Silver Connect 상담사' : '나'} · {formatDateTimeKST(comment.createdAt)}
      </Typography>
      <Box
        sx={{
          maxWidth: '85%',
          px: 2,
          py: 1.5,
          borderRadius: isAdmin ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
          bgcolor: isAdmin ? 'primary.50' : 'grey.100',
          border: '1px solid',
          borderColor: isAdmin ? 'primary.light' : 'divider',
        }}
      >
        <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', color: 'text.primary' }}>
          {comment.content}
        </Typography>
      </Box>
    </Box>
  );
}

const InquiryDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const inquiryId = id ? parseInt(id, 10) : null;

  const { inquiry, loading, error, refetch } = useInquiryDetail(inquiryId);
  const { reopen, loading: reopenLoading, error: reopenError } = useReopenInquiry();

  const [reopenContent, setReopenContent] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);


  const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
    const added = Array.from(e.target.files ?? []);
    const toAdd = added.slice(0, 5 - files.length);
    setFiles((prev) => [...prev, ...toAdd]);
    toAdd.forEach((file) => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (ev) => setFilePreviews((prev) => [...prev, ev.target?.result as string]);
        reader.readAsDataURL(file);
      } else {
        setFilePreviews((prev) => [...prev, '']);
      }
    });
    e.target.value = '';
  };

  const handleFileRemove = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setFilePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleReopen = async () => {
    if (!inquiryId || !reopenContent.trim()) return;
    setUploadError(null);
    const result = await reopen(inquiryId, reopenContent.trim());
    if (result) {
      if (files.length > 0) {
        try {
          await uploadAttachments('INQUIRY_COMMENT', result.commentId, files);
        } catch (err: any) {
          setUploadError(err.message ?? '첨부파일 업로드에 실패했습니다.');
        }
      }
      setReopenContent('');
      setFiles([]);
      setFilePreviews([]);
      refetch();
    }
  };

  const canReopen = inquiry && (inquiry.status === 'REPLIED' || inquiry.status === 'REOPENED');

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate(-1)} aria-label="뒤로가기">
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>문의 상세</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 3 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {inquiry && (
          <>
            {/* 원글 헤더 */}
            <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Chip
                  label={STATUS_CONFIG[inquiry.status].label}
                  color={STATUS_CONFIG[inquiry.status].color}
                  size="small"
                />
                <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                  {formatDateTimeKST(inquiry.createdAt)}
                </Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1.5, lineHeight: 1.5 }}>
                {inquiry.title}
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <Typography sx={{ fontSize: '1rem', lineHeight: 1.8, whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {inquiry.content}
              </Typography>
            </Paper>

            {/* 댓글 대화 영역 */}
            {inquiry.comments && inquiry.comments.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {inquiry.comments.map((comment) => (
                  <CommentBubble key={comment.id} comment={comment} />
                ))}
              </Box>
            )}

            {/* 추가 문의 입력 폼 */}
            {canReopen && (
              <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 2 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1.5 }}>추가 문의하기</Typography>
                {reopenError && <Alert severity="error" sx={{ mb: 1.5 }}>{reopenError}</Alert>}
                {uploadError && <Alert severity="warning" sx={{ mb: 1.5 }}>{uploadError}</Alert>}
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="추가 문의 내용을 입력해주세요"
                  value={reopenContent}
                  onChange={(e) => { if (e.target.value.length <= 2000) setReopenContent(e.target.value); }}
                  inputProps={{ style: { fontSize: '1rem' } }}
                  helperText={`${reopenContent.length}/2000`}
                  FormHelperTextProps={{ sx: { textAlign: 'right' } }}
                  sx={{ mb: 2 }}
                />

                {/* 파일 첨부 */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {files.map((file, idx) => (
                    <Box key={idx} sx={{ position: 'relative', width: 64, height: 64 }}>
                      {filePreviews[idx] ? (
                        <img
                          src={filePreviews[idx]}
                          alt={file.name}
                          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                        />
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
                        >
                          <Typography sx={{ fontSize: '0.6rem', textAlign: 'center', color: 'text.secondary', px: 0.5, wordBreak: 'break-all' }}>
                            {file.name}
                          </Typography>
                        </Paper>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleFileRemove(idx)}
                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', p: 0.25, border: '1px solid', borderColor: 'divider' }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                  {files.length < 5 && (
                    <label>
                      <input type="file" accept="image/*,application/pdf" multiple hidden onChange={handleFileAdd} />
                      <Box
                        sx={{
                          width: 64, height: 64, border: '1px dashed', borderColor: 'divider',
                          borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          cursor: 'pointer', '&:hover': { borderColor: 'primary.main' },
                        }}
                      >
                        <AddPhotoAlternate sx={{ color: 'text.secondary', fontSize: 28 }} />
                      </Box>
                    </label>
                  )}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={!reopenContent.trim() || reopenLoading}
                  onClick={handleReopen}
                  sx={{ py: 1.25, fontSize: '1rem', fontWeight: 700 }}
                >
                  {reopenLoading ? <CircularProgress size={22} color="inherit" /> : '접수하기'}
                </Button>
              </Paper>
            )}

            {inquiry.status === 'SUBMITTED' && (
              <Alert severity="info" sx={{ fontSize: '0.9rem' }}>
                문의가 접수되었습니다. 빠른 시일 내에 답변드리겠습니다.
              </Alert>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default InquiryDetailPage;
